import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Bell, Plus, Send, Trash2, Eye, Download, Paperclip, File,
  Loader2, AlertCircle, CheckCircle, Clock, Users, Building2,
  User, Briefcase, XCircle, Search, Filter, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-800', icon: Clock },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-800', icon: Bell },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800', icon: AlertCircle }
};

const TARGET_TYPES = {
  all: { label: 'All Users', icon: Users, description: 'Send to everyone' },
  entities: { label: 'Specific Institutes', icon: Building2, description: 'Select institutes' },
  departments: { label: 'Specific Departments', icon: Briefcase, description: 'Select departments' },
  roles: { label: 'Specific Roles', icon: User, description: 'Select roles' },
  employees: { label: 'Specific Employees', icon: User, description: 'Select individuals' }
};

export default function Notices() {
  const { user } = useAuth();
  const { entities } = useOutletContext() || {};
  const [notices, setNotices] = useState([]);
  const [sentNotices, setSentNotices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('received');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal',
    target_type: 'all',
    target_entities: [],
    target_departments: [],
    target_roles: [],
    target_employees: [],
    attachment: null,
    attachment_data: '',
    attachment_name: '',
    attachment_type: ''
  });

  const userRole = user?.role || 'employee';
  const canCreateNotice = ['super_admin', 'admin', 'hr', 'principal', 'dean', 'hod'].includes(userRole);
  const isSuperAdmin = userRole === 'super_admin';
  const isHR = userRole === 'hr';
  const isPrincipal = ['principal', 'dean'].includes(userRole);
  const isHOD = userRole === 'hod';

  // Get available target types based on role
  const getAvailableTargetTypes = () => {
    if (isSuperAdmin) {
      return ['all', 'entities', 'departments', 'roles', 'employees'];
    } else if (isHR) {
      return ['all', 'departments', 'roles', 'employees'];
    } else if (isPrincipal) {
      return ['all', 'departments', 'roles', 'employees'];
    } else if (isHOD) {
      return ['employees'];
    }
    return [];
  };

  // Get available roles based on sender role
  const getAvailableRoles = () => {
    if (isSuperAdmin) {
      return ['hr', 'principal', 'hod', 'employee'];
    } else if (isHR) {
      return ['principal', 'hod', 'employee'];
    } else if (isPrincipal) {
      return ['hod', 'employee'];
    }
    return ['employee'];
  };

  // Get unique departments from employees
  const getDepartments = () => {
    const depts = [...new Set(employees.map(e => e.department).filter(Boolean))];
    return depts.sort();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [noticesRes, sentRes, employeesRes] = await Promise.all([
        fetch(`${API_URL}/api/notices`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/notices/sent`, { headers, credentials: 'include' }),
        canCreateNotice ? fetch(`${API_URL}/api/employees`, { headers, credentials: 'include' }) : Promise.resolve({ ok: true, json: () => [] })
      ]);

      if (noticesRes.ok) setNotices(await noticesRes.json());
      if (sentRes.ok) setSentNotices(await sentRes.json());
      if (employeesRes.ok) {
        const empData = await employeesRes.json();
        setEmployees(Array.isArray(empData) ? empData : []);
      }
    } catch (error) {
      toast.error('Failed to fetch notices');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and image files (JPG, PNG) are allowed');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setFormData({
        ...formData,
        attachment: file,
        attachment_data: base64,
        attachment_name: file.name,
        attachment_type: file.type
      });
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 'normal',
      target_type: isSuperAdmin ? 'all' : (isHOD ? 'employees' : 'all'),
      target_entities: [],
      target_departments: [],
      target_roles: [],
      target_employees: [],
      attachment: null,
      attachment_data: '',
      attachment_name: '',
      attachment_type: ''
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      toast.error('Please fill in title and content');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/notices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          title: formData.title,
          content: formData.content,
          priority: formData.priority,
          target_type: formData.target_type,
          target_entities: formData.target_entities,
          target_departments: formData.target_departments,
          target_roles: formData.target_roles,
          target_employees: formData.target_employees,
          attachment_name: formData.attachment_name || null,
          attachment_data: formData.attachment_data || null,
          attachment_type: formData.attachment_type || null
        }),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Notice sent successfully');
        setIsCreateOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send notice');
      }
    } catch (error) {
      toast.error('Failed to send notice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = async (notice) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/notices/${notice.notice_id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedNotice(data);
        setIsViewOpen(true);
        // Update local state to mark as read
        setNotices(prev => prev.map(n => 
          n.notice_id === notice.notice_id ? { ...n, is_read: true } : n
        ));
      }
    } catch (error) {
      toast.error('Failed to load notice');
    }
  };

  const handleDelete = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;

    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/notices/${noticeId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Notice deleted');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete notice');
      }
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  const handleDownload = (notice) => {
    if (!notice.attachment_data) return;
    
    const link = document.createElement('a');
    link.href = `data:${notice.attachment_type};base64,${notice.attachment_data}`;
    link.download = notice.attachment_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Attachment downloaded');
  };

  const filteredNotices = notices.filter(n => 
    n.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.sender_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = notices.filter(n => !n.is_read).length;

  const getTargetDescription = (notice) => {
    if (notice.target_type === 'all') return 'All Users';
    if (notice.target_type === 'entities') return `${notice.target_entities?.length || 0} Institutes`;
    if (notice.target_type === 'departments') return `${notice.target_departments?.length || 0} Departments`;
    if (notice.target_type === 'roles') return notice.target_roles?.join(', ') || 'Selected Roles';
    if (notice.target_type === 'employees') return `${notice.target_employees?.length || 0} Employees`;
    return 'Unknown';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Notices & Announcements
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {canCreateNotice ? 'Create and manage notices for your organization' : 'View notices and announcements'}
          </p>
        </div>
        {canCreateNotice && (
          <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-notice-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Notice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-[#DC2626]" />
                  Create New Notice
                </DialogTitle>
                <DialogDescription>
                  Send notices to employees, departments, or specific roles
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label>Title <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Notice title"
                    data-testid="notice-title"
                  />
                </div>

                {/* Content */}
                <div className="space-y-2">
                  <Label>Content <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Write your notice content here..."
                    rows={4}
                    data-testid="notice-content"
                  />
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger data-testid="notice-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Target Audience */}
                <div className="space-y-2">
                  <Label>Send To</Label>
                  <Select 
                    value={formData.target_type} 
                    onValueChange={(v) => setFormData({ ...formData, target_type: v, target_entities: [], target_departments: [], target_roles: [], target_employees: [] })}
                  >
                    <SelectTrigger data-testid="notice-target-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableTargetTypes().map(type => (
                        <SelectItem key={type} value={type}>
                          <div className="flex items-center gap-2">
                            {React.createElement(TARGET_TYPES[type].icon, { className: "w-4 h-4" })}
                            {TARGET_TYPES[type].label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Entity Selection (for Super Admin) */}
                {formData.target_type === 'entities' && isSuperAdmin && entities?.length > 0 && (
                  <div className="space-y-2">
                    <Label>Select Institutes</Label>
                    <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg max-h-40 overflow-y-auto">
                      {entities.map(entity => (
                        <label key={entity.entity_id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.target_entities.includes(entity.entity_id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, target_entities: [...formData.target_entities, entity.entity_id] });
                              } else {
                                setFormData({ ...formData, target_entities: formData.target_entities.filter(id => id !== entity.entity_id) });
                              }
                            }}
                          />
                          <span className="text-sm">{entity.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Department Selection */}
                {formData.target_type === 'departments' && getDepartments().length > 0 && (
                  <div className="space-y-2">
                    <Label>Select Departments</Label>
                    <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg max-h-40 overflow-y-auto">
                      {getDepartments().map(dept => (
                        <label key={dept} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.target_departments.includes(dept)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, target_departments: [...formData.target_departments, dept] });
                              } else {
                                setFormData({ ...formData, target_departments: formData.target_departments.filter(d => d !== dept) });
                              }
                            }}
                          />
                          <span className="text-sm">{dept}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Role Selection */}
                {formData.target_type === 'roles' && (
                  <div className="space-y-2">
                    <Label>Select Roles</Label>
                    <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg">
                      {getAvailableRoles().map(role => (
                        <label key={role} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.target_roles.includes(role)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, target_roles: [...formData.target_roles, role] });
                              } else {
                                setFormData({ ...formData, target_roles: formData.target_roles.filter(r => r !== role) });
                              }
                            }}
                          />
                          <span className="text-sm capitalize">{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Employee Selection */}
                {formData.target_type === 'employees' && employees.length > 0 && (
                  <div className="space-y-2">
                    <Label>Select Employees</Label>
                    <div className="p-3 border rounded-lg max-h-48 overflow-y-auto space-y-2">
                      {employees.map(emp => (
                        <label key={emp.employee_id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                          <Checkbox
                            checked={formData.target_employees.includes(emp.employee_id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, target_employees: [...formData.target_employees, emp.employee_id] });
                              } else {
                                setFormData({ ...formData, target_employees: formData.target_employees.filter(id => id !== emp.employee_id) });
                              }
                            }}
                          />
                          <div>
                            <span className="text-sm font-medium">{emp.first_name} {emp.last_name}</span>
                            <span className="text-xs text-slate-500 ml-2">{emp.department}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachment */}
                <div className="space-y-2">
                  <Label>Attachment (Optional)</Label>
                  <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    {formData.attachment ? (
                      <div className="flex items-center justify-center gap-2">
                        <File className="w-8 h-8 text-blue-500" />
                        <div className="text-left">
                          <p className="font-medium text-sm">{formData.attachment_name}</p>
                          <p className="text-xs text-slate-500">{(formData.attachment.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => {
                          setFormData({ ...formData, attachment: null, attachment_data: '', attachment_name: '', attachment_type: '' });
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}>
                          <XCircle className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Paperclip className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                          Attach File
                        </Button>
                        <p className="text-xs text-slate-500 mt-2">PDF, JPG, PNG (Max 5MB)</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" className="btn-primary" disabled={submitting} data-testid="send-notice-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Notice
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Received</p>
                <p className="text-2xl font-bold">{notices.length}</p>
              </div>
              <Bell className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unread</p>
                <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        {canCreateNotice && (
          <>
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Sent by You</p>
                    <p className="text-2xl font-bold text-green-600">{sentNotices.length}</p>
                  </div>
                  <Send className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">With Attachments</p>
                    <p className="text-2xl font-bold text-purple-600">{notices.filter(n => n.has_attachment).length}</p>
                  </div>
                  <Paperclip className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search notices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full mb-4 ${canCreateNotice ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <TabsTrigger value="received">
            Received {unreadCount > 0 && <Badge className="ml-2 bg-red-500 text-white">{unreadCount}</Badge>}
          </TabsTrigger>
          {canCreateNotice && <TabsTrigger value="sent">Sent ({sentNotices.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="received">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#DC2626]" />
                Received Notices
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-20 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                  ))}
                </div>
              ) : filteredNotices.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium">No notices found</h3>
                  <p className="text-slate-500">You'll see notices here when they're sent to you</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredNotices.map(notice => {
                    const priorityConfig = PRIORITY_CONFIG[notice.priority] || PRIORITY_CONFIG.normal;
                    const PriorityIcon = priorityConfig.icon;
                    return (
                      <div
                        key={notice.notice_id}
                        className={`p-4 border rounded-lg cursor-pointer hover:shadow-md transition-shadow ${!notice.is_read ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200' : ''}`}
                        onClick={() => handleView(notice)}
                        data-testid={`notice-${notice.notice_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {!notice.is_read && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                              <h3 className="font-semibold">{notice.title}</h3>
                              <Badge className={priorityConfig.color}>
                                <PriorityIcon className="w-3 h-3 mr-1" />
                                {priorityConfig.label}
                              </Badge>
                              {notice.has_attachment && (
                                <Paperclip className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{notice.content}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>From: {notice.sender_name} ({notice.sender_role})</span>
                              <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleView(notice); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {canCreateNotice && (
          <TabsContent value="sent">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#DC2626]" />
                  Sent Notices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sentNotices.length === 0 ? (
                  <div className="text-center py-12">
                    <Send className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium">No notices sent yet</h3>
                    <p className="text-slate-500">Create a notice to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sentNotices.map(notice => {
                      const priorityConfig = PRIORITY_CONFIG[notice.priority] || PRIORITY_CONFIG.normal;
                      return (
                        <div
                          key={notice.notice_id}
                          className="p-4 border rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{notice.title}</h3>
                                <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
                                {notice.has_attachment && <Paperclip className="w-4 h-4 text-slate-400" />}
                              </div>
                              <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{notice.content}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span>To: {getTargetDescription(notice)}</span>
                                <span>Read by: {notice.read_by?.length || 0}</span>
                                <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleView(notice)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(notice.notice_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* View Notice Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#DC2626]" />
              Notice Details
            </DialogTitle>
          </DialogHeader>
          {selectedNotice && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-2">
                <Badge className={PRIORITY_CONFIG[selectedNotice.priority]?.color}>
                  {PRIORITY_CONFIG[selectedNotice.priority]?.label}
                </Badge>
                {selectedNotice.has_attachment && (
                  <Badge variant="outline"><Paperclip className="w-3 h-3 mr-1" />Attachment</Badge>
                )}
              </div>
              
              <h2 className="text-2xl font-bold">{selectedNotice.title}</h2>
              
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>From: <strong>{selectedNotice.sender_name}</strong> ({selectedNotice.sender_role})</span>
                <span>{new Date(selectedNotice.created_at).toLocaleString()}</span>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg whitespace-pre-wrap">
                {selectedNotice.content}
              </div>

              {selectedNotice.has_attachment && selectedNotice.attachment_data && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <File className="w-10 h-10 text-blue-500" />
                        <div>
                          <p className="font-medium">{selectedNotice.attachment_name}</p>
                          <p className="text-sm text-slate-500">{selectedNotice.attachment_type}</p>
                        </div>
                      </div>
                      <Button onClick={() => handleDownload(selectedNotice)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                    
                    {/* Preview for images */}
                    {selectedNotice.attachment_type?.startsWith('image/') && (
                      <div className="mt-4 border rounded-lg overflow-hidden">
                        <img
                          src={`data:${selectedNotice.attachment_type};base64,${selectedNotice.attachment_data}`}
                          alt={selectedNotice.attachment_name}
                          className="max-w-full h-auto"
                        />
                      </div>
                    )}
                    
                    {/* Preview for PDFs */}
                    {selectedNotice.attachment_type === 'application/pdf' && (
                      <div className="mt-4 border rounded-lg overflow-hidden" style={{ height: '400px' }}>
                        <iframe
                          src={`data:application/pdf;base64,${selectedNotice.attachment_data}`}
                          title={selectedNotice.attachment_name}
                          className="w-full h-full"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setIsViewOpen(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
